import { Controller } from "@hotwired/stimulus"

const CATEGORY_LABELS = {
  grocery:       "Groceries",
  dining:        "Dining",
  gas:           "Gas",
  travel:        "Travel",
  entertainment: "Entertainment",
  streaming:     "Streaming",
  pharmacy:      "Pharmacy",
  utility:       "Utilities",
  default:       "Everything Else"
}

export default class extends Controller {
  static targets = ["input", "results", "cardType"]
  static values  = { cards: Array }

  calculate() {
    const spending = {}
    this.inputTargets.forEach(el => {
      spending[el.dataset.category] = parseFloat(el.value) || 0
    })

    const onlyNoFee = this.cardTypeTarget.value === "no_fee"

    const ranked = this.cardsValue
      .filter(card => !onlyNoFee || card.no_fee)
      .map(card => {
        let annual = 0
        Object.entries(spending).forEach(([cat, monthly]) => {
          const rule = card.rules[cat] || card.rules["default"]
          if (rule && monthly > 0) {
            annual += (monthly * 12) * (rule.value / 100)
          }
        })
        return { card, annual: Math.round(annual * 100) / 100 }
      })
      .filter(r => r.annual > 0)
      .sort((a, b) => b.annual - a.annual)
      .slice(0, 10)

    this.renderResults(ranked, spending)
  }

  renderResults(ranked, spending) {
    const filledCategories = Object.entries(spending).filter(([, v]) => v > 0)
    const totalMonthly = filledCategories.reduce((s, [, v]) => s + v, 0)
    const totalAnnual  = totalMonthly * 12

    if (ranked.length === 0) {
      this.resultsTarget.innerHTML = `<p class="text-sm text-gray-400 py-4 text-center">Enter your monthly spending above to see results.</p>`
      return
    }

    const fmt = n => n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    const fmtDec = n => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    const categoryBreakdown = filledCategories
      .map(([cat, amt]) => `<span class="inline-flex items-center gap-1 bg-gray-100 rounded px-2 py-0.5 text-xs text-gray-600"><span class="font-medium">${CATEGORY_LABELS[cat] || cat}</span> $${fmt(amt)}/mo</span>`)
      .join("")

    const rows = ranked.map((r, i) => {
      const feeNote = r.card.no_fee
        ? `<span class="text-green-600 font-medium">No annual fee</span>`
        : `${r.card.fee} annual fee`
      const img = r.card.image_url
        ? `<a href="${r.card.path}" class="flex-shrink-0"><img src="${r.card.image_url}" alt="${r.card.name}" class="w-16 h-10 object-contain rounded"></a>`
        : `<div class="w-16 h-10 flex-shrink-0 rounded overflow-hidden bg-gradient-to-br from-slate-700 to-slate-900 flex flex-col justify-between p-1.5">
            <div class="w-[28%] aspect-[4/3] bg-yellow-400/70 rounded-[2px]"></div>
            <p class="text-white/90 text-[7px] font-semibold leading-tight line-clamp-2">${r.card.name}</p>
          </div>`
      return `
        <li class="flex items-center gap-3 p-3 sm:p-4 rounded-lg ${i === 0 ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50'}">
          <span class="text-sm font-bold ${i === 0 ? 'text-blue-600' : 'text-gray-400'} w-6 text-center flex-shrink-0">#${i + 1}</span>
          ${img}
          <div class="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-3">
            <div class="flex-1 min-w-0">
              <a href="${r.card.path}" class="text-sm font-semibold text-gray-900 hover:text-blue-600 transition-colors leading-snug line-clamp-2">${r.card.name}</a>
              <p class="text-xs text-gray-500 mt-0.5">${feeNote}</p>
            </div>
            <div class="flex items-center gap-2 mt-1.5 sm:mt-0 sm:flex-shrink-0">
              <div class="flex-1 sm:flex-none sm:text-right">
                <p class="text-sm sm:text-lg font-bold ${i === 0 ? 'text-blue-600' : 'text-gray-700'}">$${fmtDec(r.annual)}</p>
                <p class="text-xs text-gray-400">est. rewards/yr</p>
              </div>
              <a href="${r.card.path}" class="text-xs bg-blue-600 hover:bg-blue-700 text-white font-semibold px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap flex-shrink-0">View Card</a>
            </div>
          </div>
        </li>`
    }).join("")

    this.resultsTarget.innerHTML = `
      <div class="mb-4 space-y-2">
        <div class="flex flex-wrap gap-1.5">${categoryBreakdown}</div>
        <p class="text-xs text-gray-400">
          $${fmt(totalMonthly)}/month · $${fmt(totalAnnual)}/year total spending · estimated gross rewards before annual fees
        </p>
      </div>
      <ol class="space-y-2">${rows}</ol>
    `
  }
}
